import { ConfigContext } from '@/contextAPI/configContext';
import styled from '@emotion/styled';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Divider,
  Drawer,
  DrawerProps,
  Grid,
  IconButton,
  Link,
  Palette,
  Paper,
  Tooltip,
  Typography
} from '@mui/material';
import { get } from 'lodash';
import { useContext, useEffect, useRef, useState } from 'react';
import { convertStylesToStyled, getLineageMetaTitle, insertWordBreaks } from '../../_utils/utils';
import { OrderJobTask, UserProfile } from '../../redux/slices/slice_types';
import { LabelValue } from '../_common';
import UserDisplay from '../_common/AssigneeSelect/UserDisplay';
import ClickCopy from '../_common/ClickCopy/ClickCopy';
import History from '../_common/History/History';
import Refresh from '../_common/Refresh';
import PolarisIcon from '../_common/PolarisIcon';
import TableStatus from '../_common/Table/TableStatus';
import TaskResolve from '../_common/Tasks/TaskResolve';
import TaskType from '../_common/Tasks/TaskType';
import useConfirmNavigation from '../_hooks/useConfirmNavigation';
import { useEventListener } from '../_hooks/useEventListener';
import TaskDrawerExtraMeta from './TaskDrawerExtraMeta';
import useConfirmationPrompt from '../_hooks/useConfirmationPrompt';
import { getData } from '@/redux/slices/dataTableSlice';
import AssigneeSelect from '../_common/AssigneeSelect/AssigneeSelect';
import DateTimeDisplay from '../_common/DateTimeDisplay';
import { getAgeFromCreatedAndCompleted } from '../../_utils/date';
import TaskAction from '../_common/Tasks/TaskAction';
import { EditableDate, Notes } from '../../exports/columnCommonComponents';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { updateJobDueDate } from '@/redux/slices/jobSlice';
import { selectCanWriteJobs } from '@/redux/selectors/authSelectors';
import { useToast } from '../_common/Toast';
import TaskDrawerBulkActions from './TaskDrawerBulkActions';

export type TaskDrawerProps = {
  isDirty: boolean;
  task: OrderJobTask | null;
  onDrawerClose: () => void;
  popoutOnly?: boolean;
  loading?: boolean;
  onRefresh: (id: string | number) => void;
  setActiveTask: (task: OrderJobTask) => void;
  setIsDirty: (val: boolean) => void;
  source?: 'tasks' | 'processing-exceptions';
};

const DRAWER_ANIMATION_DURATION = 250;

const TaskDrawer = ({
  task,
  source,
  onDrawerClose,
  popoutOnly = false,
  loading = false,
  onRefresh,
  setActiveTask,
  setIsDirty,
  isDirty
}: TaskDrawerProps) => {
  const dispatch = useAppDispatch();
  const [anchor, setAnchor] = useState<DrawerProps['anchor']>('right');
  const [open, setOpen] = useState(false);
  const customFields = useContext(ConfigContext)?.taskConfig?.customFields || {};
  const displaySeries = task?.job?.title?.entity_type === 'program';
  const lineageMetaTitle = getLineageMetaTitle(task?.job?.title, !displaySeries);
  const LabelValueProps: { color: keyof Palette } = { color: 'primary' };
  const formRef = useRef<any>(null);
  const canWriteJobs = useAppSelector(selectCanWriteJobs);
  const { error } = useToast();

  useEffect(() => {
    setOpen(!!popoutOnly || !!task?.id);
  }, [popoutOnly, task]);

  const prompt = useConfirmNavigation({
    block: isDirty,
    title: 'Unsaved Task Changes',
    message: 'Are you sure you want to discard your task changes?'
  });

  const onCloseConfirm = () => {
    let timeout = 0;

    if (!popoutOnly) {
      setOpen(false);
      timeout = DRAWER_ANIMATION_DURATION;

      setTimeout(() => {
        onDrawerClose();
      }, timeout);
    } else {
      window.close();
    }
  };

  const handlePopOut = () => {
    onDrawerClose();
    if (!task) return;
    window.open(`/tasks/?id=${task.id}&popout=1&source=${source}`, 'popout', `popup=1,height=600,width=400,top=0,left=-${window.screen.width}`);
  };

  const handler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (popoutOnly) {
        window.close();
      } else {
        onClose();
      }
    }
  };

  const onSubmit = () => {
    formRef.current?.handleSubmit();
    setIsDirty(false);
  };

  useEventListener('keydown', handler);

  const { dialog: confirmationPrompt, setDialogVisible } = useConfirmationPrompt({
    title: 'Unsaved Task Changes',
    message: 'Are you sure you want to discard your task changes?',
    onConfirm: onCloseConfirm
  });

  const onClose = () => {
    if (isDirty) {
      setDialogVisible(true);
    } else {
      onCloseConfirm();
    }
  };

  const handleRefresh = () => {
    if (task?.id) {
      onRefresh(task.id);
    }
    dispatch(getData());
  };

  const renderCustomFields = () =>
    Object.keys(customFields).map((field) => {
      const fieldLocation = field.split('__').join('.');
      const value = get(task, fieldLocation);

      // if `forTypes` exists, only show if task type is in the list
      if (customFields[field].forTypes && !customFields[field].forTypes.includes(task?.name)) {
        return null;
      }

      if (customFields[field].source !== undefined && source !== customFields[field].source) {
        return null;
      }

      return (
        <LabelValue key={field} label={customFields[field].label} size={{ xs: customFields[field].cols || 6 }} {...LabelValueProps}>
          {customFields[field].render ? customFields[field].render(value) : value}
        </LabelValue>
      );
    });

  const onJobEdit = (key: any, value: any) => {
    dispatch(updateJobDueDate({ id: task?.job?.id as any, value }))
      .unwrap()
      .then(() => handleRefresh())
      .catch((e: any) => error(e.message));
  };

  if (!task) return null;

  const allFieldsReadOnly = Object.values(task.extra_meta_schema?.properties || {}).every((field: any) => field.readonly || field.readOnly);
  const isReadOnly =
    allFieldsReadOnly ||
    task.extra_meta_schema?.readonly ||
    task.extra_meta_schema?.readOnly ||
    ['complete', 'completed', 'complete_with_errors', 'cancelled', 'cancelling', 'accepted', 'approved', 'approving'].includes(task?.status || '');

  return (
    <FormattedDrawer
      variant="persistent"
      anchor={anchor}
      open={open}
      onClose={onClose}
      popout={`${popoutOnly}`}
      hideBackdrop
      transitionDuration={{ enter: popoutOnly ? 0 : DRAWER_ANIMATION_DURATION, exit: DRAWER_ANIMATION_DURATION }}
    >
      {prompt}
      {confirmationPrompt}
      {!popoutOnly && (
        <>
          <ActionBar container>
            <Tooltip title={`Dock to ${anchor === 'right' ? 'left' : 'right'}`}>
              <IconButton onClick={() => setAnchor(anchor === 'right' ? 'left' : 'right')}>
                <PolarisIcon icon={anchor === 'right' ? 'dock_left' : 'dock_right'} height={20} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Pop out">
              <IconButton onClick={handlePopOut}>
                <PolarisIcon icon="popup" height={20} />
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose as any} sx={{ ml: 'auto' }}>
              <PolarisIcon icon="close" />
            </IconButton>
          </ActionBar>
          <Divider sx={{ mx: 2 }} />
        </>
      )}

      <Grid
        container
        alignItems="flex-start"
        spacing={4}
        sx={{
          p: 3.5,
          pt: 4,
          opacity: loading ? 0.4 : 1,
          pointerEvents: loading ? 'none' : 'auto'
        }}
      >
        <Grid sx={{ py: 1, ml: 2, mb: -2 }} container alignItems="center" justifyContent="space-between">
          {source === 'processing-exceptions' && !!task?.status_reason && task.status !== 'resolved' && (
            <TaskResolve button task={task} onResolutionSubmitted={handleRefresh} />
          )}
          <RefreshButton onClick={handleRefresh} color="inherit" />
        </Grid>
        <LabelValue label="Task ID" {...LabelValueProps}>
          <strong>{task?.hid}</strong>
        </LabelValue>
        <LabelValue label="TaskType" {...LabelValueProps}>
          <TaskType type={task?.step} prettyName={task?.pretty_name} />
        </LabelValue>
        <LabelValue label="Status" {...LabelValueProps}>
          <TableStatus
            status={task.status}
            labelOverride={source === 'processing-exceptions' ? task.pretty_name : undefined}
            sx={{ '& span': { whiteSpace: 'normal' } }}
          />
        </LabelValue>
        <LabelValue label="Title" size={{ xs: 12 }} {...LabelValueProps}>
          <Typography variant="h6">{displaySeries ? task?.job?.title?.lineage_meta?.series_name : task?.job?.title?.name}</Typography>
          {lineageMetaTitle && <Typography variant="caption">{lineageMetaTitle}</Typography>}
        </LabelValue>
        {task?.job?.version?.rally_asset_name || task?.input_data?.rally_asset_name ? (
          <LabelValue label="Rally Asset" size={{ xs: 12 }} {...LabelValueProps}>
            {task?.job?.version?.rally_asset_name ?? task?.input_data?.rally_asset_name}
          </LabelValue>
        ) : null}
        {!task.job?.version?.program_id && (
          <LabelValue label="Version" size={{ xs: 7 }} {...LabelValueProps}>
            {task?.job?.version?.name}
          </LabelValue>
        )}
        {task.task_action_input?.new_version_id && (
          <LabelValue label="Output Version ID" size={{ xs: 5 }} {...LabelValueProps}>
            {task.task_action_input?.new_version_id}
          </LabelValue>
        )}
        <Grid item xs={7}>
          <EditableDate
            label="Job Due Date"
            key="due_date"
            color="primary"
            onEdit={onJobEdit}
            onEditFormatter={(date: Date) => date.toISOString()}
            disabled={!canWriteJobs}
            onEditKey="due_date"
            value={task?.job?.due_date || undefined}
            disablePastDates
          >
            <DateTimeDisplay singleLine>{task?.job?.due_date}</DateTimeDisplay>
          </EditableDate>
        </Grid>
        <LabelValue label="Age" size={{ xs: 5 }} {...LabelValueProps}>
          {getAgeFromCreatedAndCompleted(task.created_at, task.completed_at)}
        </LabelValue>
        <LabelValue label="Assignee" size={{ xs: 7 }} {...LabelValueProps}>
          <AssigneeSelect assigns="user" update="task" value={(task.assigned_user as UserProfile) || null} id={task.id} />
        </LabelValue>
        <LabelValue label="Related Job" size={{ xs: 5 }} {...LabelValueProps}>
          <Link href={`/jobs/${task.job?.id}`} target={popoutOnly ? '_blank' : '_self'} rel="noreferrer">
            J{task.job?.id}
          </Link>
        </LabelValue>
        <LabelValue label="Updated at" size={{ xs: 7 }} {...LabelValueProps}>
          <DateTimeDisplay singleLine>{task?.updated_at}</DateTimeDisplay>
        </LabelValue>
        <LabelValue label="Action" size={{ xs: 5 }} {...LabelValueProps}>
          <TaskAction task={task} onRefresh={() => onRefresh(task?.id)} />
        </LabelValue>

        {/* CUSTOM CLIENT FIELDS FROM CONFIG */}
        {renderCustomFields()}

        <LabelValue label="Notes" {...LabelValueProps} size={{ xs: 3 }}>
          <Notes data={task} />
        </LabelValue>
        <LabelValue label="History" {...LabelValueProps} size={{ xs: 4 }}>
          <History data={task} iconOnly />
        </LabelValue>

        <TaskDrawerBulkActions task={task} handleRefresh={handleRefresh} />

        {task.status_reason && (
          <>
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Grid container alignItems="center" sx={{ mb: 1 }}>
                  <Box sx={{ mr: 1, height: 16 }}>
                    <PolarisIcon
                      icon={task.status === 'resolved' ? 'complete_duotone' : 'error'}
                      height={16}
                      style={{ color: task.status === 'resolved' ? 'var(--success-main)' : 'var(--warning-main)' }}
                    />
                  </Box>
                  <Typography variant="caption">Error Details</Typography>
                </Grid>
                <div style={{ maxHeight: 150, overflow: 'auto' }}>
                  <ClickCopy copyText={task.status_reason}>{insertWordBreaks(task.status_reason) || '-'}</ClickCopy>
                </div>
                {source === 'processing-exceptions' && task.status === 'resolved' && (
                  <Alert sx={{ p: 1, px: 2, mt: 2 }} icon={false}>
                    <AlertTitle sx={{ display: 'flex', whiteSpace: 'nowrap', fontSize: '.9rem', mb: 1 }}>
                      Resolved by
                      <span style={{ paddingLeft: 5 }}>
                        <UserDisplay user={task.resolved_by || null} />
                      </span>
                    </AlertTitle>
                    {task.resolution_notes}
                  </Alert>
                )}
              </Paper>
            </Grid>
          </>
        )}

        {!!Object.keys(task?.extra_meta_schema || {}).length && (
          <Grid item xs={12}>
            <TaskDrawerExtraMeta
              onRefresh={() => onRefresh(task.id)}
              key={task.id}
              onTaskChanged={() => setIsDirty(true)}
              ref={formRef}
              task={task}
              setActiveTask={setActiveTask}
            />
            {!isReadOnly && (
              <StickyFooter anchor={anchor}>
                <Button variant="text" color="default" type="submit" sx={{ mt: 2 }} onClick={onClose as any}>
                  Cancel
                </Button>
                <Button variant="contained" color="primary" type="submit" sx={{ mt: 2 }} onClick={onSubmit}>
                  Save
                </Button>
              </StickyFooter>
            )}
          </Grid>
        )}
      </Grid>
    </FormattedDrawer>
  );
};

export default TaskDrawer;

const RefreshButton = styled(Refresh)`
  opacity: 0.6;
  margin-right: ${({ theme }) => theme.spacing(-0.5)};
  margin-left: auto;
  transition: ease opacity 250ms;

  &.processing-exceptions {
    top: 0;
  }

  &:hover {
    opacity: 0.8;
    transition: ease opacity 250ms;
  }

  span {
    top: ${({ theme }) => theme.spacing(1)};
  }
`;

type FormattedDrawerProps = {
  popout: string;
  footer?: string;
};

const FormattedDrawer = styled(Drawer)<FormattedDrawerProps>`
  ${({ theme }) => convertStylesToStyled(theme.customOverrides.TaskDrawer)}

  .MuiDrawer-paper {
    width: 100%;
    transition: ease opacity 250ms;
    font-size: 14px;

    ${({ theme }) => theme.breakpoints.up('sm')} {
      max-width: ${({ popout }) => (popout === 'true' ? 'none' : '420px')};
    }
  }
`;

const ActionBar = styled(Grid)`
  padding: ${({ theme }) => theme.spacing(0, 2)};
  margin: ${({ theme }) => theme.spacing(1.5, 0, 1)};

  .MuiIconButton-root {
    opacity: 0.5;
    transition: ease opacity 250ms;

    &:hover {
      transition: ease opacity 250ms;
      opacity: 0.6;
    }
  }
`;

const StickyFooter = styled.div<{ anchor?: string }>`
  ${(props) => (props.anchor === 'left' ? `left: 0;` : `right: 0;`)};
  position: fixed;
  width: 420px;
  bottom: 0;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  padding: ${({ theme }) => theme.spacing(2, 3.5)};
  column-gap: ${({ theme }) => theme.spacing(2)};
  background-color: ${({ theme }) => theme.palette.background.paper};
  border-left: 1px solid ${({ theme }) => theme.palette.divider};
  transition: ease opacity 250ms;
  z-index: 9;

  & > button {
    margin: 0px;
  }
`;
